
export const pillars = [
  {
    id: "obe",
    icon: "Target",
    title: "Outcome-Based Education",
    subtitle: "OBE",
    color: "from-blue-700 to-blue-900",
    description:
      "OBE is the architectural spine of GBSRC's curriculum. Every course is structured around clearly defined Course Outcomes (COs), mapped to Programme Outcomes (POs), Programme Specific Outcomes (PSOs), and Graduate Attributes.",
    points: [
      "Systematic CO-PO-PSO mapping ensures curriculum coherence and accountability",
      "Graduate Attributes encompass analytical ability, communication, leadership, and ethics",
      "Continuous monitoring through AoL cycles enables evidence-based curriculum improvement",
      "Shifts the question from 'What was taught?' to 'What can the student do?'",
    ],
  },
  {
    id: "experiential",
    icon: "Compass",
    title: "Experiential Learning",
    subtitle: "Kolb's Theory",
    color: "from-indigo-700 to-indigo-900",
    description:
      "Grounded in Kolb's Experiential Learning Theory, GBSRC emphasises management competence built through doing, reflecting, conceptualising, and experimenting with real business situations.",
    points: [
      "Live industry projects and consulting assignments with partner organisations",
      "Field immersion, market surveys, and corporate visits",
      "Business labs, simulations, role plays, and startup projects",
      "Community engagement initiatives linking education to social impact",
    ],
  },
  {
    id: "case",
    icon: "BookOpen",
    title: "Case-Based Learning",
    subtitle: "Harvard Method",
    color: "from-sky-700 to-sky-900",
    description:
      "Inspired by the Harvard case method, GBSRC integrates case pedagogy extensively across all functional areas using both Indian and global cases covering leadership, ethics, strategy, and operations.",
    points: [
      "Develops critical thinking, decision-making, and analytical rigour",
      "Builds communication and persuasion through structured discussions",
      "Fosters team-based problem-solving and divergent thinking",
      "Bridges theoretical frameworks with practitioner reality",
    ],
  },
  {
    id: "industry",
    icon: "Briefcase",
    title: "Industry-Integrated Learning",
    subtitle: "Live Interface",
    color: "from-teal-700 to-teal-900",
    description:
      "Industry participation is structurally embedded at GBSRC. The institution maintains a continuous interface with the corporate world ensuring classroom learning remains current, contextual, and career-relevant.",
    points: [
      "Guest lectures and practitioner-led sessions from senior industry leaders",
      "Corporate mentorship and Management Development Programmes (MDPs)",
      "Structured internship programmes with performance-linked assessments",
      "Live business problem-solving and industry-sponsored capstone projects",
    ],
  },
  {
    id: "ai",
    icon: "Brain",
    title: "AI-Embedded Learning",
    subtitle: "Future-Ready",
    color: "from-violet-700 to-violet-900",
    description:
      "GBSRC has systematically integrated AI into its pedagogy — not as a novelty but as a core professional competency. Students use AI as an augmentation tool while retaining the primacy of human judgment.",
    points: [
      "AI tools for research synthesis, competitive analysis, and business intelligence",
      "AI-assisted case analysis and scenario modelling",
      "Ethics of AI: bias, accountability, transparency, and responsible deployment",
      "Analytics and data visualisation using industry-standard platforms",
    ],
  },
  {
    id: "simulation",
    icon: "Layers",
    title: "Simulation-Based Learning",
    subtitle: "Safe Complexity",
    color: "from-purple-700 to-purple-900",
    description:
      "Business simulations create safe, consequence-rich environments where students experience the full complexity of managerial decision-making. Mistakes become learning events and feedback is immediate.",
    points: [
      "Marketing, financial, operations, and entrepreneurship simulations",
      "Negotiation exercises and crisis management scenarios",
      "Real-time feedback loops reinforcing analytical and strategic skills",
      "Dynamic environments mirroring real managerial complexity",
    ],
  },
  {
    id: "collaborative",
    icon: "Users",
    title: "Collaborative & Peer Learning",
    subtitle: "Social Construction",
    color: "from-cyan-700 to-cyan-900",
    description:
      "GBSRC structures much of its curriculum around collaborative activity. Students develop interpersonal, leadership, and conflict-resolution competencies through sustained team-based work.",
    points: [
      "Cross-functional team projects and group presentations",
      "Peer evaluation frameworks building accountability and feedback skills",
      "Collaborative case discussions simulating boardroom dynamics",
      "Leadership and conflict resolution through real team challenges",
    ],
  },
  {
    id: "reflective",
    icon: "Lightbulb",
    title: "Reflective & Self-Directed",
    subtitle: "Metacognition",
    color: "from-blue-600 to-blue-800",
    description:
      "GBSRC cultivates metacognitive awareness — the ability to think about one's own thinking. Students maintain learning portfolios and reflective journals to build professional identity and lifelong learning.",
    points: [
      "Reflective journals integrating theory with lived professional experience",
      "Learning portfolios documenting competency development over the programme",
      "Self-assessment against graduate attributes fostering personal accountability",
      "Lifelong learning orientation built progressively from day one",
    ],
  },
];

export const methods = [
  {
    icon: "Repeat",
    title: "Flipped Classroom",
    description:
      "Students engage with curated materials before class — readings, videos, podcasts, data sets — freeing classroom time for discussion, debate, and peer learning. Promotes active over passive engagement.",
  },
  {
    icon: "Search",
    title: "Problem-Based Learning",
    description:
      "Students solve unstructured, real-world business problems requiring independent research, data interpretation, strategic thinking, and collaborative decision-making. Builds tolerance for ambiguity.",
  },
  {
    icon: "Sparkles",
    title: "Design Thinking",
    description:
      "Adapted from Stanford's d.school methodology: empathise, define, ideate, prototype, and test. Students tackle complex societal and business challenges through human-centred, iterative innovation.",
  },
  {
    icon: "BarChart2",
    title: "Analytics & Data-Driven",
    description:
      "Every student is trained in evidence-based decision-making: business analytics, dashboard creation, financial modelling, predictive analysis, and digital decision platforms. Quantitative literacy is non-negotiable.",
  },
  {
    icon: "Globe",
    title: "Multidisciplinary Learning",
    description:
      "Aligned with NEP 2020's holistic vision, GBSRC integrates management with technology, economics, psychology, communication, sustainability, and public policy for multi-dimensional problem-solving.",
  },
];

export const assessmentPairs = [
  { method: "Case analysis and business reports", competency: "Conceptual understanding and application" },
  { method: "Business simulations and role plays", competency: "Critical thinking and analytical rigour" },
  { method: "Viva voce and oral examinations", competency: "Oral and written communication" },
  { method: "Industry assignments and live projects", competency: "Leadership and team collaboration" },
  { method: "Business plans and startup pitches", competency: "Problem-solving and decision-making" },
  { method: "Data analysis and dashboard tasks", competency: "Digital and data literacy" },
  { method: "Open-book analytical examinations", competency: "Ethical reasoning and professional behaviour" },
  { method: "Reflective journals and portfolios", competency: "Innovation and entrepreneurial thinking" },
  { method: "Group discussions and presentations", competency: "Global and cross-cultural awareness" },
  { method: "Peer and self-evaluation exercises", competency: "Self-awareness and lifelong learning" },
];

export const aolSteps = [
  { step: "01", title: "Define Learning Goals", desc: "Articulate programme-level learning goals tied to graduate attributes and industry expectations." },
  { step: "02", title: "Outcome Mapping", desc: "Map goals to Course Outcomes, Programme Outcomes, and Programme Specific Outcomes." },
  { step: "03", title: "Direct Assessment", desc: "Collect performance data through rubric-scored case analyses, projects, presentations, and examinations." },
  { step: "04", title: "Indirect Assessment", desc: "Gather alumni surveys, employer feedback, exit surveys, and graduate tracking data." },
  { step: "05", title: "Data Analysis", desc: "Analyse attainment levels against pre-set benchmarks using statistical and qualitative methods." },
  { step: "06", title: "Closing the Loop", desc: "Implement curriculum revisions, faculty development, or pedagogical adjustments based on evidence." },
];

export const benchmarks = [
  { practice: "Experiential Learning", impl: "Live projects, simulations, field immersion, consulting assignments" },
  { practice: "Case Method (Harvard)", impl: "Extensive case pedagogy across all functional domains with Indian and global cases" },
  { practice: "Analytics Orientation (Wharton)", impl: "Dashboard creation, data analysis, financial modelling, predictive analytics" },
  { practice: "AI Integration (MIT)", impl: "AI-embedded learning, responsible AI use, AI ethics in curriculum" },
  { practice: "Outcome-Based Education", impl: "CO-PO-PSO mapping, graduate attribute framework, AoL cycles" },
  { practice: "Industry Engagement", impl: "MDPs, internships, live industry assignments, practitioner sessions" },
  { practice: "Competency Assessment", impl: "Capability-based multi-modal evaluation; rubric-scored assessments" },
  { practice: "Entrepreneurship Focus", impl: "Startup incubation, pitch competitions, venture ideation ecosystem" },
  { practice: "Ethical Orientation", impl: "Responsible management education integrated across curriculum" },
  { practice: "Multidisciplinary (NEP 2020)", impl: "Integrated curriculum spanning management, technology, social sciences" },
  { practice: "Flipped Classroom", impl: "Pre-class preparation enabling active, discussion-based classroom sessions" },
  { practice: "Design Thinking (Stanford)", impl: "Empathy, ideation, prototyping, and iterative problem-solving embedded" },
  { practice: "Global Orientation (INSEAD)", impl: "International cases, cross-cultural management, digital globalisation" },
  { practice: "Research Integration", impl: "Applied research, market studies, data-driven investigation" },
  { practice: "Technology Infrastructure", impl: "LMS, smart classrooms, AI tools, hybrid learning systems" },
];

export const focusAreas = [
  {
    icon: "TrendingUp",
    title: "Employability & Career Readiness",
    items: [
      "Communication excellence: written, oral, digital, and cross-cultural",
      "Presentation and storytelling skills for professional environments",
      "Leadership, team collaboration, and conflict management",
      "Corporate etiquette and professional behaviour",
      "Digital fluency with industry-standard platforms",
    ],
  },
  {
    icon: "Rocket",
    title: "Entrepreneurship & Innovation",
    items: [
      "Startup incubation support with structured mentorship",
      "Business plan competitions and venture ideation exercises",
      "Entrepreneur interaction series: access to founder experiences",
      "Pitch presentation training building persuasive communication",
      "Innovation projects linked to UN Sustainable Development Goals",
    ],
  },
  {
    icon: "Shield",
    title: "Ethics, ESG & Sustainable Leadership",
    items: [
      "Business ethics integrated into case discussions and classroom culture",
      "Corporate governance, accountability, and transparency as anchors",
      "Sustainability embedded in strategic management courses",
      "Social responsibility and inclusive growth as decision lenses",
      "Exposure to responsible business models and social entrepreneurship",
    ],
  },
  {
    icon: "Monitor",
    title: "Technology-Enabled Infrastructure",
    items: [
      "Learning Management Systems (LMS) for resource delivery and analytics",
      "Smart classrooms enabling multimedia instruction and live data analysis",
      "Digital collaboration tools supporting remote and hybrid teamwork",
      "AI-enabled adaptive learning support customising the student journey",
      "Hybrid learning systems ensuring equity and accessibility",
    ],
  },
];

export const globalSchools = [
  { school: "Harvard Business School", focus: "Case Method Pedagogy" },
  { school: "Stanford d.school", focus: "Design Thinking" },
  { school: "MIT Media Lab / Sloan", focus: "Innovation & Technology Integration" },
  { school: "Wharton School", focus: "Analytics Orientation" },
  { school: "INSEAD", focus: "Multicultural & Global Management" },
  { school: "AACSB / EFMD / EQUIS", focus: "Assurance of Learning" },
];

export const stats = [
  { number: "8", label: "Core Pedagogical Pillars" },
  { number: "5", label: "Innovative Teaching Methods" },
  { number: "10+", label: "Assessment Modalities" },
  { number: "15", label: "Global Benchmarks" },
  { number: "3", label: "Accreditation Frameworks" },
  { number: "6", label: "AoL Quality Steps" },
];
